import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { API_BASE_URL } from '../config/apiConfig';

const STATIC_DOCTORS = [
  { id: 1, name: 'Dr. Tanya Chauhan', specialty: 'Orthodontist, Braces Specialist & Implantologist' },
];

const REASONS = [
  'Dental Implants Consultation',
  'Single Sitting Root Canal Treatment (RCT)',
  'Orthodontic Braces / Clear Aligners (Invisalign)',
  '3D Intraoral Digital Scanning',
  'Carestream Digital OPG X-Ray',
  'Teeth Whitening & Smile Makeover',
  'Sparkling Tooth Jewelry',
  'Routine Dental Checkup & Cleaning',
  'Wisdom Tooth Pain / Extraction',
  'General Dental Consultation',
  'Other',
];

const AppointmentsPage = () => {
  const [doctors, setDoctors] = useState(STATIC_DOCTORS);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  useEffect(() => {
    axios.get(`${API_BASE_URL}/doctors`)
      .then(res => setDoctors(res.data && res.data.length > 0 ? res.data : STATIC_DOCTORS))
      .catch(() => setDoctors(STATIC_DOCTORS));
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    setApiError('');
    try {
      const payload = {
        ...data,
        doctorId: parseInt(data.doctorId),
        appointmentDate: new Date(data.appointmentDate).toISOString(),
      };
      await axios.post(`${API_BASE_URL}/appointments`, payload);
      setSubmitted(true);
      reset();
    } catch (err) {
      setApiError(err.response?.data?.message || 'Failed to book appointment. Please call us directly at +91 95361 13530.');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  today.setDate(today.getDate() + 1);
  const minDate = today.toISOString().split('T')[0];

  return (
    <>
      {/* Page Hero */}
      <section className="page-hero" id="appointments-hero">
        <div className="container">
          <div className="page-hero-inner">
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <span>Book Appointment</span>
            </div>
            <h1>Book a Dental Appointment</h1>
            <p>Schedule your consultation with Dr. Tanya Chauhan & team. We confirm promptly.</p>
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: 'var(--color-light)' }} id="appointment-form-section">
        <div className="container">
          <div className="contact-grid" style={{ gridTemplateColumns: '1fr 1.8fr', gap: '2.5rem' }}>

            {/* Info Column */}
            <div>
              <div className="contact-info-card" id="appointment-info-card">
                <h3>Why Consult with Us?</h3>
                <p>Our dental specialists are available Monday to Saturday: 9:30 AM – 1:00 PM & 4:30 PM – 8:00 PM.</p>

                {[
                  { icon: '🦷', title: 'Internationally Trained', desc: '12+ years expertise in orthodontics, implants & smile design' },
                  { icon: '🔍', title: '3D Digital Dentistry', desc: '3Shape 3D Intraoral Scanner & Kodak Digital OPG X-Ray' },
                  { icon: '✨', title: 'Family Wali Feeling', desc: 'Warm, transparent, patient-first conservative approach' },
                  { icon: '📞', title: 'Quick Confirmation', desc: 'Appointment confirmed promptly via Phone / WhatsApp' },
                ].map((item, i) => (
                  <div className="contact-detail" key={i} id={`appt-benefit-${i}`}>
                    <div className="contact-detail-icon">{item.icon}</div>
                    <div className="contact-detail-text">
                      <strong>{item.title}</strong>
                      <span>{item.desc}</span>
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: 'rgba(225,36,84,0.15)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(225,36,84,0.3)' }}>
                  <p style={{ color: 'white', fontWeight: 600, marginBottom: '0.5rem' }}>🚨 Need Immediate Help?</p>
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>For appointment queries and assistance, call or WhatsApp:</p>
                  <a href="tel:+919536113530" style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', display: 'block', marginTop: '0.5rem' }}>
                    📞 +91 95361 13530
                  </a>
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="form-card" id="appointment-form-card">
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '2rem 0' }} id="appointment-success">
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', color: 'var(--color-navy)', marginBottom: '1rem' }}>
                    Dental Appointment Booked Successfully!
                  </h3>
                  <p style={{ color: 'var(--color-text-muted)', marginBottom: '1rem', lineHeight: 1.7 }}>
                    Thank you! Our team will confirm your appointment slot shortly via email or phone call.
                    You will receive all instructions for your visit to Global Dental Clinic ™.
                  </p>
                  <p style={{ color: '#e12454', fontWeight: 600, marginBottom: '2rem' }}>
                    For urgent queries, call: <a href="tel:+919536113530">+91 95361 13530</a>
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={() => setSubmitted(false)} id="book-another-btn">
                      Book Another Appointment
                    </button>
                    <Link to="/" className="btn btn-outline-navy">Return to Home</Link>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="form-title">Schedule Your Consultation</h2>
                  <p className="form-subtitle">Fill in the details below. All fields marked * are required.</p>

                  {apiError && (
                    <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', padding: '1rem', color: 'var(--color-error)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                      ⚠️ {apiError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} id="appointment-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label" htmlFor="patientName">Patient Name *</label>
                        <input
                          id="patientName"
                          className={`form-input ${errors.patientName ? 'error' : ''}`}
                          placeholder="Your full name"
                          {...register('patientName', { required: 'Patient name is required' })}
                        />
                        {errors.patientName && <span className="form-error">{errors.patientName.message}</span>}
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="appt-phone">Phone Number *</label>
                        <input
                          id="appt-phone"
                          className={`form-input ${errors.phone ? 'error' : ''}`}
                          placeholder="+91 95361 13530"
                          {...register('phone', { required: 'Phone number is required', pattern: { value: /^[0-9+\-\s]{10,14}$/, message: 'Enter a valid phone number' } })}
                        />
                        {errors.phone && <span className="form-error">{errors.phone.message}</span>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="appt-email">Email Address *</label>
                      <input
                        id="appt-email"
                        type="email"
                        className={`form-input ${errors.email ? 'error' : ''}`}
                        placeholder="your@email.com"
                        {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email' } })}
                      />
                      {errors.email && <span className="form-error">{errors.email.message}</span>}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label" htmlFor="appt-doctor">Select Specialist *</label>
                        <select
                          id="appt-doctor"
                          className={`form-select ${errors.doctorId ? 'error' : ''}`}
                          {...register('doctorId', { required: 'Please select a doctor' })}
                        >
                          <option value="">Choose a Dental Specialist...</option>
                          {doctors.map(d => (
                            <option key={d.id} value={d.id}>{d.name} — {d.specialty}</option>
                          ))}
                        </select>
                        {errors.doctorId && <span className="form-error">{errors.doctorId.message}</span>}
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="appt-date">Preferred Date *</label>
                        <input
                          id="appt-date"
                          type="date"
                          min={minDate}
                          className={`form-input ${errors.appointmentDate ? 'error' : ''}`}
                          {...register('appointmentDate', { required: 'Please select a date' })}
                        />
                        {errors.appointmentDate && <span className="form-error">{errors.appointmentDate.message}</span>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="appt-reason">Reason for Visit *</label>
                      <select
                        id="appt-reason"
                        className={`form-select ${errors.reason ? 'error' : ''}`}
                        {...register('reason', { required: 'Please select a reason' })}
                      >
                        <option value="">Select reason for dental visit...</option>
                        {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      {errors.reason && <span className="form-error">{errors.reason.message}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="appt-notes">Additional Notes (Optional)</label>
                      <textarea
                        id="appt-notes"
                        className="form-textarea"
                        placeholder="Any specific symptoms, toothache details, or requests..."
                        rows={4}
                        {...register('notes')}
                      />
                    </div>

                    <div style={{ background: 'rgba(143,181,105,0.1)', border: '1px solid rgba(143,181,105,0.3)', borderRadius: 'var(--radius-md)', padding: '1rem', marginBottom: '1.5rem', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                      <FaCheckCircle style={{ color: '#5b8235', marginRight: '0.5rem' }} />
                      By submitting, you agree to our privacy policy. Your information will only be used for appointment scheduling.
                    </div>

                    <button
                      type="submit"
                      id="appt-submit-btn"
                      className="btn btn-primary"
                      disabled={loading}
                      style={{ width: '100%', justifyContent: 'center', fontSize: '1rem', padding: '1rem' }}
                    >
                      {loading ? (
                        <>⏳ Booking Your Appointment...</>
                      ) : (
                        <>Book Dental Appointment <FaArrowRight /></>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="section-padding-sm" style={{ background: 'white' }} id="how-it-works">
        <div className="container">
          <div className="section-header">
            <div className="badge">Simple Process</div>
            <h2 className="section-title">How to Book an Appointment</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', position: 'relative' }}>
            {[
              { step: '01', icon: '📋', title: 'Fill the Form', desc: 'Enter your personal details, choose a specialist and preferred date.' },
              { step: '02', icon: '✅', title: 'Confirmation', desc: 'Our team will contact you via Phone or WhatsApp to confirm your slot.' },
              { step: '03', icon: '🏥', title: 'Visit the Clinic', desc: 'Arrive 10 minutes early at Bodakdev or C.G. Road branch.' },
              { step: '04', icon: '🦷', title: 'Meet Your Doctor', desc: 'Consultation with Dr. Tanya Chauhan.' },
            ].map((item, i) => (
              <div key={i} id={`how-step-${i}`} style={{ textAlign: 'center', padding: '2rem 1.5rem', background: 'var(--color-light)', borderRadius: 'var(--radius-lg)', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #e12454 0%, #8fb569 100%)', color: 'white', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>
                  {item.step}
                </div>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{item.icon}</div>
                <h4 style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', marginBottom: '0.5rem' }}>{item.title}</h4>
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AppointmentsPage;
